import { t } from '@lingui/macro'
import { useFormikContext } from 'formik'
import { useEffect, useState } from 'react'
import styled, { useTheme } from 'styled-components'

import { LegacyMedicationForm } from '@/@bothrs-ui/web/components/forms/medication/LegacyMedicationForm'
import {
  filterOutNoMedicationSentinel,
  isNoMedicationSentinel,
  type LegacyMedicationStatement,
  NO_MEDICATION_SENTINEL,
} from '@/@bothrs-ui/web/components/forms/medication/legacy-medication.helpers'
import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'

type LegacyMedicationQuestionProps = {
  questionId: string
  value?: LegacyMedicationStatement[]
  onNext: () => void
}

export const LegacyMedicationQuestion = ({ questionId, value, onNext }: LegacyMedicationQuestionProps) => {
  // Hooks
  // ------------------------------------------------------------------------- /
  const theme = useTheme()
  const { setFieldValue, values } = useFormikContext<Record<string, unknown>>()

  // Template variables
  // ------------------------------------------------------------------------- /

  // Use Formik as single source of truth - read directly from Formik values
  const medications: LegacyMedicationStatement[] = (() => {
    const formikValue = values[questionId]
    if (Array.isArray(formikValue) && formikValue.length > 0) {
      return formikValue as LegacyMedicationStatement[]
    }
    // Fallback to value prop if Formik doesn't have it yet (initial render)
    return value ?? []
  })()

  const hasRealMedications = medications.length > 0 && !isNoMedicationSentinel(medications)

  // State
  // ------------------------------------------------------------------------- /
  const [choice, setChoice] = useState<'add' | 'none' | null>(null)
  const [showForm, setShowForm] = useState(false)

  // Derive initial state from Formik values after reinitialization.
  // useState initializer runs before Formik's enableReinitialize restores saved values,
  // so we sync via useEffect once the correct data is available.
  useEffect(() => {
    if (choice !== null || showForm) return
    if (medications.length > 0 && !isNoMedicationSentinel(medications)) {
      setShowForm(true)
    }
  }, [choice, showForm, medications.length])

  // Interaction handlers
  // ------------------------------------------------------------------------- /
  const handleChoiceSelect = (newChoice: 'add' | 'none') => {
    if (newChoice === 'none') {
      setFieldValue(questionId, [NO_MEDICATION_SENTINEL])
    } else if (newChoice === 'add') {
      // Clear the "no medication" sentinel when switching to add
      setFieldValue(questionId, filterOutNoMedicationSentinel(medications))
    }
    setChoice(newChoice)
  }

  const handleChoiceNext = () => {
    if (choice === 'add') {
      setShowForm(true)
    } else {
      // 'none' → advance to next question
      onNext()
    }
  }

  const handleAddMedication = (medication: LegacyMedicationStatement) => {
    // Filter out the "no medication" placeholder if it exists
    const currentMeds = filterOutNoMedicationSentinel(medications)
    setFieldValue(questionId, [...currentMeds, medication])
  }

  const handleEditMedication = (index: number, medication: LegacyMedicationStatement) => {
    const updated = medications.map((med, index_) => (index_ === index ? medication : med))
    setFieldValue(questionId, updated)
  }

  const handleRemoveMedication = (index: number) => {
    const remaining = medications.filter((_, index_) => index_ !== index)

    if (remaining.length === 0) {
      // All medications removed — save as "no medication" and stay in form view
      setFieldValue(questionId, [NO_MEDICATION_SENTINEL])
    } else {
      setFieldValue(questionId, remaining)
    }
  }

  // Render
  // ------------------------------------------------------------------------- /

  // Form screen: shown when real medications exist or user chose to add
  if (hasRealMedications || showForm) {
    return (
      <FlexColumn gap={theme.UI.Spacing.Space4}>
        <LegacyMedicationForm
          medications={medications}
          onAdd={handleAddMedication}
          onEdit={handleEditMedication}
          onRemove={handleRemoveMedication}
        />
        <Button type='button' variant='Primary' onClick={onNext} disabled={medications.length === 0}>
          {t({
            id: 'globals.continue',
            comment: 'Global for continue',
            message: 'Ga verder',
          })}
        </Button>
      </FlexColumn>
    )
  }

  // Choice screen: shown when no medication data exists or only the "no medication" sentinel
  return (
    <FlexColumn gap={theme.UI.Spacing.Space3}>
      <StMedicationItem type='BodyMediumRegular'>
        {t({
          id: 'medication.emptyState',
          message: 'Nog geen medicatiegegevens gekend',
        })}
      </StMedicationItem>
      <StChoiceOption htmlFor={`${questionId}_add`}>
        <input
          type='radio'
          id={`${questionId}_add`}
          name={`${questionId}_medication_choice`}
          checked={choice === 'add'}
          onChange={() => handleChoiceSelect('add')}
        />
        <FlexColumn>
          <span>
            {t({
              id: 'medication.choice.add',
              message: 'Ik wil medicatie toevoegen',
            })}
          </span>
        </FlexColumn>
      </StChoiceOption>
      <StChoiceOption htmlFor={`${questionId}_none`}>
        <input
          type='radio'
          id={`${questionId}_none`}
          name={`${questionId}_medication_choice`}
          checked={choice === 'none'}
          onChange={() => handleChoiceSelect('none')}
        />
        <FlexColumn>
          <span>
            {t({
              id: 'medication.choice.none',
              message: 'Ik neem geen medicatie',
            })}
          </span>
        </FlexColumn>
      </StChoiceOption>
      <Button type='button' variant='Primary' onClick={handleChoiceNext} disabled={choice === null}>
        {t({
          id: 'globals.continue',
          comment: 'Global for continue',
          message: 'Ga verder',
        })}
      </Button>
    </FlexColumn>
  )
}

const StMedicationItem = styled(Span)`
  padding: ${({ theme }) => theme.UI.SpacingPx.Space3}
    ${({ theme }) => theme.UI.SpacingPx.Space4};
  background-color: ${({ theme }) => theme.Colors.Neutrals.Lighter};
`

const StChoiceOption = styled.label`
  &:hover {
    cursor: pointer;

    > div {
      transition-property: border, background, color;
      transition-duration: 175ms;
      background-color: ${({ theme }) => theme.Components.Conversational.ActiveFill};
      color: ${({ theme }) => theme.Components.Conversational.ActiveText};
    }
  }

  > input {
    display: none;
  }

  > div {
    border: 1px solid ${({ theme }) => theme.Components.Conversational.Border};
    border-radius: ${({ theme }) => theme.UI.BorderRadius.Small}px;
    background-color: ${({ theme }) => theme.Components.Conversational.Fill};
    color: ${({ theme }) => theme.Components.Conversational.Text};
    padding: ${({ theme }) => `${theme.UI.SpacingPx.Space3} ${theme.UI.SpacingPx.Space4}`};
    transition: all 175ms ease-out;
  }

  input:checked + div {
    border-color: ${({ theme }) => theme.Components.Conversational.ActiveBorder};
    background-color: ${({ theme }) => theme.Components.Conversational.ActiveFill};
    color: ${({ theme }) => theme.Components.Conversational.ActiveText};
  }
`
